import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useReactiveVar } from '@apollo/client';
import axios, { AxiosProgressEvent } from 'axios';
import download from 'downloadjs';

import { companyIdVar } from '../companies/company.service';
import { BASE_API_URL, sleep } from '../../../lib/utils';
import { IAsset, Identifiable, IDownloadFileParams, IRequestTokenParams } from '../../../lib/types';
import { REMOVE_ASSET, REMOVE_ASSET_WITH_TOKEN } from './asset.queries';
import { ISuccessResponse } from '../../../lib/query.types';
import { UploadItemData } from '../../_editor/upload/upload.types';
import { uniqueId } from 'lodash';
import { tokenVar } from '../../../providers/token.service';

export const NO_FILES_ERROR = 'No files to download';

const downloadAsset = async ({
  Authorization,
  fileId,
  onDownloadProgress,
  requestToken
}: IDownloadFileParams & { fileId: string }) => {
  if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });

  return axios<Blob>({
    baseURL: BASE_API_URL,
    method: 'GET',
    url: '/api/asset/' + fileId,
    headers: requestToken ? undefined : { Authorization },
    params: requestToken ? { requestToken } : undefined,
    responseType: 'blob',
    onDownloadProgress
  });
};

export const useDownloadAsset = ({ fileId }: UploadItemData, requestToken?: string) => {
  const Authorization = useReactiveVar(tokenVar);

  const handleDownloadAsset = useCallback(
    async (fileName: string) => {
      if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });
      if (!fileId) return Promise.reject({ message: 'No file to download.' });

      const baseMessage = 'Downloading ' + fileName;
      const toastId = 'download-' + fileName;
      toast.loading(baseMessage, { id: toastId });

      return downloadAsset({
        Authorization,
        fileId,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.progress && progressEvent)
            if (progressEvent.progress < 1)
              toast.success(baseMessage + ' ' + Math.round(progressEvent.progress * 100) + '%', { id: toastId });
        },
        requestToken
      })
        .then((file) => {
          toast.success(fileName + ' finished downloading!', { id: toastId });
          if (file) download(file.data, fileName);
        })
        .catch(() => toast.error(`Failed to download ${fileName}`, { id: toastId }));
    },
    [Authorization, fileId, requestToken]
  );

  return { downloadAsset: handleDownloadAsset };
};

const downloadMultipleAssets = async ({
  Authorization,
  assets,
  folderName,
  onDownloadProgress,
  requestToken
}: IDownloadFileParams & { assets: { id: string; fileName: string }[]; folderName: string }) => {
  if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });

  return axios<Blob>({
    baseURL: BASE_API_URL,
    method: 'POST',
    url: '/api/asset/multiple',
    headers: requestToken ? undefined : { Authorization },
    params: requestToken ? { requestToken } : undefined,
    responseType: 'blob',
    onDownloadProgress,
    data: { assets, folderName }
  });
};

export const useDownloadAllAssets = ({
  requestToken
}: { activeSection?: number | null; requestToken?: string } = {}) => {
  const Authorization = useReactiveVar(tokenVar);

  const handleDownloadAssets = useCallback(
    async ({ archiveName = 'assests', items }: { archiveName?: string; items: UploadItemData[] }) => {
      if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });

      const itemCount = items.filter((i) => i.fileId).length;
      if (!itemCount) return Promise.reject({ message: NO_FILES_ERROR });

      const baseMessage = `Downloading ${itemCount} files`;
      const toastId = 'download-all-' + uniqueId();
      toast.loading(baseMessage, { id: toastId });

      let failed = false;
      const onDownloadProgress = (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.progress && progressEvent)
          if (progressEvent.progress < 1)
            toast.success(baseMessage + ' ' + Math.round(progressEvent.progress * 100) + '%', { id: toastId });
          // Sleep a small amount so that failed count can catch up
          else {
            toast.success('Finished downloading!', { id: toastId });
            sleep(50).then(() => {
              if (failed) toast.error(`Failed to download`, { id: toastId });
            });
          }
      };

      downloadMultipleAssets({
        Authorization,
        assets: items.map((i) => {
          // Use the label that was already calculated by findFilesToDownload()
          // which includes the correct localTitle prefix from data structure recursion
          const fileName = i.label || i.fileName
          return { id: i.fileId ?? '', fileName };
        }),
        folderName: archiveName,
        onDownloadProgress,
        requestToken
      })
        .then((file) => {
          if (file) download(file.data, `${archiveName}.zip`);
        })
        .catch(() => {
          failed = true;
        });
    },
    [Authorization, requestToken]
  );

  return { downloadAssets: handleDownloadAssets };
};

export const useRemoveAsset = (requestId?: string, requestToken?: string) => {
  const [mutation, rest] = useMutation<{ removeAsset: ISuccessResponse }, Identifiable>(REMOVE_ASSET, {
    context: { serializationKey: 'MUTATION', tracked: true }
    // TODO: Update cache when we start reading assets from cache
  });

  const [mutationWithToken, restWithToken] = useMutation<
    { removeAssetWithToken: ISuccessResponse },
    Identifiable & IRequestTokenParams & { requestId?: string }
  >(REMOVE_ASSET_WITH_TOKEN, {
    context: { serializationKey: 'MUTATION', tracked: true }
    // TODO: Update cache when we start reading assets from cache
  });

  const removeAsset = useCallback(
    (variables: Identifiable) =>
      requestToken && requestId
        ? mutationWithToken({ variables: { ...variables, requestToken, requestId } })
        : mutation({ variables }),
    [mutation, mutationWithToken, requestId, requestToken]
  );

  return { removeAsset, ...(requestToken ? restWithToken : rest) };
};

export const useUploadAsset = (requestToken?: string) => {
  const companyId = useReactiveVar(companyIdVar);
  const Authorization = useReactiveVar(tokenVar);

  const uploadAsset = useCallback(
    async (file: File, options?: { isPublic?: boolean }) => {
      if (!requestToken && (!Authorization || !companyId)) return Promise.reject({ message: 'Unauthorized.' });

      const formData = new FormData();
      formData.append('file', file);
      if (requestToken) formData.append('requestToken', requestToken);
      else if (companyId) formData.append('companyId', companyId);

      // Add isPublic parameter if provided
      if (options?.isPublic !== undefined) {
        formData.append('isPublic', String(options.isPublic));
      }

      const baseMessage = 'Uploading ' + file.name;
      toast(baseMessage, { id: 'upload-' + file.name });
      return axios<{ asset: IAsset }>({
        baseURL: BASE_API_URL,
        method: 'POST',
        url: '/api/asset',
        data: formData,
        headers: requestToken ? undefined : { Authorization },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.progress)
            toast.success(
              progressEvent.progress === 1
                ? file.name + ' finished uploading!'
                : baseMessage + ' ' + Math.round(progressEvent.progress * 100) + '%',
              {
                id: 'upload-' + file.name
              }
            );
        }
      });
    },
    [Authorization, companyId, requestToken]
  );

  return { uploadAsset };
};

export const useGetAssetViewUrl = (assetId: string, requestToken?: string) => {
  const Authorization = useReactiveVar(tokenVar);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchViewUrl = useCallback(async () => {
    if (!requestToken && !Authorization) {
      setError(new Error('Unauthorized'));
      return null;
    }
    if (!assetId) {
      setError(new Error('No asset ID provided'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${BASE_API_URL}/api/asset/${assetId}/view-url${requestToken ? `?requestToken=${requestToken}` : ''}`;
      const response = await axios.get<{ url: string; expiresAt: number }>(url, {
        headers: Authorization ? { Authorization } : {},
      });

      setViewUrl(response.data.url);
      setLoading(false);
      return response.data.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to get asset view URL');
      setError(errorMessage);
      setLoading(false);
      console.error('Failed to fetch asset view URL:', err);
      return null;
    }
  }, [Authorization, assetId, requestToken]);

  return { viewUrl, loading, error, fetchViewUrl };
};
